/**
 * ============================================================
 * Project.js — Individual Project Page
 * ============================================================
 *
 * Loads:
 *   -> Project info (GET /api/projects/:id)
 *   -> Related people (faculty, members, alumni)
 *   -> Related publications
 *
 * Backend returns:
 *   {
 *     projectData: { ... },
 *     personData: [...],          // project members
 *     publicationData: [...]      // related publications
 *   }
 **/

import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Container } from "react-bootstrap";

import SynlabNavbar from "../components/SynlabNavbar";
import SynlabFooter from "../components/SynlabFooter";
import PersonCard from "../components/PersonCard";
import PubCardCompact from "../components/PubCardCompact";

import "./css/Page.css";     // global page layout
import "./css/Subpage.css";  // shared layout for subpages
import "./css/Project.css";  // page-specific styling

import { projectPhoto } from "../utils/mediaPaths";
import { sortPubsByYearAndTitle } from "../utils/sortPubs";

// ------------------------------------------------------------
// Component
// ------------------------------------------------------------
function Project() {
  const { id } = useParams();

  const [project, setProject] = useState({});
  const [faculty, setFaculty] = useState([]);
  const [members, setMembers] = useState([]);
  const [alumni, setAlumni] = useState([]);
  const [publications, setPublications] = useState([]);

  const [showMorePubs, setShowMorePubs] = useState(false);

  // ----------------------------------------------------------
  // Load project + members + publications
  // ----------------------------------------------------------
  useEffect(() => {
    async function load() {

      try {
        const res = await fetch(`/api/projects/${id}`);
        const data = await res.json();

        // Project data
        setProject(data.projectData || {});

        // Publications associated with project
        const pubs = data.publicationData || [];
        setPublications(pubs);        

        // Get the raw peole records
        const peopleRaw = data.personData || [];

        // Convert raw people data into a normalized people object
        const people = peopleRaw.map((p) => ({
          ...p,
          id: p.person_id ?? p.id,
          position: Number(p.person_position ?? p.position ?? 0),
          faculty: Number(p.person_faculty ?? p.faculty ?? 0),
          project_alumni: Number(p.project_alumni ?? 0),
        }));

        // Sort and categorize members
        // 1 - Active project members (non-faculty)
        const activeMembers = people
          .filter((u) => u.faculty === 0 && u.project_alumni === 0)
          .sort((a, b) => a.position - b.position);

        // 2 - Active faculty
        const activeFaculty = people
          .filter((u) => u.faculty === 1 && u.project_alumni === 0)
          .sort((a, b) => a.position - b.position);

        // 3 - Alumni (non-faculty)
        const alumniNonFaculty = people
          .filter((u) => u.faculty === 0 && u.project_alumni === 1)
          .sort((a, b) => a.position - b.position);

        // 4 - Alumni (faculty)
        const alumniFaculty = people
          .filter((u) => u.faculty === 1 && u.project_alumni === 1)
          .sort((a, b) => a.position - b.position);

        setMembers(activeMembers);
        setFaculty(activeFaculty);

        // Alumni: non-faculty first, faculty second
        setAlumni([...alumniNonFaculty, ...alumniFaculty]);
      } catch (err) {
        console.error("Failed to load project", err);
      }
    }

    load();
  }, [id]);

  if (!project) return null; // Initial safeguard


  // ----------------------------------------------------------
  // Year formatting
  // ----------------------------------------------------------
  const start = project.startyear || null;
  const end = project.past ? project.endyear : "Present";
  const yearRange = start ? `${start}–${end}` : "";


  // ----------------------------------------------------------
  // Determine media availability
  // ----------------------------------------------------------
  function getProjectMedia(p) {
    const hasPhoto = p.photo && p.photo !== "null";
    const hasVideo = p.video && p.video !== "null";

    return {
      hasPhoto,
      hasVideo,
      photoSrc: projectPhoto(p.photo),
      videoSrc: hasVideo ? p.video : null
    };
  }

  const media = getProjectMedia(project);


  // ----------------------------------------------------------
  // Render
  // ----------------------------------------------------------
  return (
    <div className="page-wrapper">

      {/* ----- NAVBAR ----- */}
      <header className="header">
        <SynlabNavbar />
      </header>

      {/* ----- FLEX COLUMN WRAPPER ----- */}
      <div className="inner-page-wrapper">

        {/* ----- HEADER IMAGE (without title overlay) ----- */}
        <div className="header-image-container">
          <img
            src="/headers/Header_project.png"
            alt="Project Header"
            className="header-image"
          />
        </div>

        {/* ----- MAIN CONTENT ----- */}
        <section className="page-content">
          <Container className="page-inner" fluid="lg">

            {/* ----- PROJECT NAME + YEARS ----- */}
            <div className="project-name-year-header">
              <h2 className="page-header">{project.name}</h2>
              {yearRange && <h3 className="page-subheader">{yearRange}</h3>}
            </div>

            {/* ----- BOTH PHOTO + VIDEO side-by-side ----- */}
            {media.hasPhoto && media.hasVideo && (
              <div className="project-media-row">
                <div className="project-photo-wrapper">
                  <img
                    className="project-photo"
                    src={media.photoSrc}
                    alt={project.name}
                  />
                </div>

                <div className="project-video-wrapper">
                  <iframe
                    src={media.videoSrc}
                    title={`Project video — ${project.name}`}
                    allow="autoplay; fullscreen; picture-in-picture"
                  />
                </div>
              </div>
            )}

            {/* ----- ONLY PHOTO ----- */}
            {media.hasPhoto && !media.hasVideo && (
              <div className="project-photo-wrapper single-photo">
                <img
                  className="project-photo"
                  src={media.photoSrc}
                  alt={project.name}
                />
              </div>
            )}

            {/* ----- ONLY VIDEO ----- */}
            {!media.hasPhoto && media.hasVideo && (
              <div className="project-media-row">
                <div className="project-video-wrapper">
                  <iframe
                    src={media.videoSrc}
                    title={`Project video — ${project.name}`}
                    allow="autoplay; fullscreen; picture-in-picture"
                  />
                </div>
              </div>
            )}

            {/* ---- NO PHOTO OR VIDEO - use default image ---- */}
            {!media.hasPhoto && !media.hasVideo && (
              <div className="project-photo-wrapper single-photo">
                <img
                  className="project-photo"
                  src="/images/default-project.jpg"
                  alt="Default project"
                />
              </div>
            )}

            {/* ----- PROJECT DESCRIPTION ----- */}
            <div className="project-desc-wrapper">
              <p className="project-desc-text">{project.description}</p>

              {/* Project Website */}
              {project.url && project.url !== "null" && (
                <p className="project-website-line">
                  <span className="project-website-label">Website:</span>
                  <a
                    href={project.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="project-website-value link"
                  >
                    {project.url}
                  </a>
                </p>
              )}
            </div>

            {/* ----- PROJECT MEMBERS ----- */}
            {members.length > 0 && (
              <div className="project-section">
                <h2 className="page-header">Project Members</h2>
                <div className="page-container">
                  {members.map(person => (
                    <PersonCard key={person.id} person={person} />
                  ))}
                </div>
              </div>
            )}

            {/* ----- FACULTY ----- */}
            {faculty.length > 0 && (
              <div className="project-section">
                <h2 className="page-header">Faculty</h2>
                <div className="page-container">
                  {faculty.map(person => (
                    <PersonCard key={person.id} person={person} />
                  ))}
                </div>
              </div>
            )}

            {/* ---- PROJECT ALUMNI (non-faculty first, faculty second) ---- */}
            {alumni.length > 0 && (
              <div className="project-section">
                <h2 className="page-header">Past Members</h2>
                <div className="page-container">
                  {alumni.map(person => (
                    <PersonCard key={person.id} person={person} />
                  ))}
                </div>
              </div>
            )}

            {/* ----- PUBLICATIONS ----- */}
            {publications.length > 0 && (
              <div className="project-section">
                <h2 className="page-header">Publications</h2>

                  {(() => {
                    // Use sortPubs utility to sort pubs by year and title
                    const { flattened } = sortPubsByYearAndTitle(publications);

                    // Apply "show more" limit
                    const visible = showMorePubs ? flattened : flattened.slice(0, 5);

                    return (
                      <>
                        <div className="project-pubs-list">
                          {visible.map((pub, idx) => (
                            <PubCardCompact key={idx} publication={pub} />
                          ))}
                        </div>

                        {flattened.length > 5 && (
                          <button
                            className="show-more-button"
                            onClick={() => setShowMorePubs(!showMorePubs)}
                          >
                            {showMorePubs ? "Show Less" : "Show More"}
                          </button>
                        )}
                      </>
                    );
                  })()}

              </div>
            )}

          </Container>
        </section>
      </div>

      {/* ----- FOOTER ----- */}
      <SynlabFooter />
    </div>
  );
}

export default Project;
